<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { getPaypalClientId } from '../../../../api/saasBilling';

const { t } = useI18n();
const store = useStore();

const subscription = computed(
  () => store.getters['saasBilling/getSubscription']
);
const plans = computed(() => store.getters['saasBilling/getPlans']);
const isFetching = computed(() => store.getters['saasBilling/isFetching']);
const isProcessingPayment = computed(
  () => store.getters['saasBilling/isProcessingPayment']
);

const selectedPlan = ref(null);
const selectedMonths = ref(1);
const showPaymentSection = ref(false);
const paypalLoaded = ref(false);
const paypalButtonsRef = ref(null);

const daysRemaining = computed(
  () => subscription.value?.days_remaining ?? null
);
const isExpired = computed(() => subscription.value?.expired ?? false);
const statusBadgeClass = computed(() => {
  if (!subscription.value) return 'bg-n-slate-3 text-n-slate-11';
  if (isExpired.value) return 'bg-red-100 text-red-700';
  if (daysRemaining.value <= 7) return 'bg-amber-100 text-amber-700';
  return 'bg-green-100 text-green-700';
});

const statusLabel = computed(() => {
  if (!subscription.value) return t('SAAS_BILLING.CURRENT_PLAN.NO_PLAN');
  if (isExpired.value) return t('SAAS_BILLING.CURRENT_PLAN.EXPIRED');
  return t('SAAS_BILLING.CURRENT_PLAN.ACTIVE');
});

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const totalAmount = computed(() => {
  if (!selectedPlan.value) return 0;
  return (selectedPlan.value.price_month * selectedMonths.value).toFixed(2);
});

const renderPayPalButtons = () => {
  if (!paypalButtonsRef.value || !window.paypal || !selectedPlan.value) return;
  paypalButtonsRef.value.innerHTML = '';

  window.paypal
    .Buttons({
      createOrder: (data, actions) => {
        return actions.order.create({
          purchase_units: [
            {
              description: `${selectedPlan.value.name} - ${selectedMonths.value} mes(es)`,
              amount: { currency_code: 'USD', value: totalAmount.value },
            },
          ],
        });
      },
      onApprove: async (data, actions) => {
        try {
          const details = await actions.order.capture();
          const result = await store.dispatch('saasBilling/createPayment', {
            planId: selectedPlan.value.id,
            months: selectedMonths.value,
            paypalOrderId: data.orderID,
            paypalCaptureId: details.id,
          });
          if (result.success) {
            useAlert(t('SAAS_BILLING.PAYMENT.SUCCESS'));
            showPaymentSection.value = false;
            selectedPlan.value = null;
          } else {
            useAlert(result.error || t('SAAS_BILLING.PAYMENT.ERROR'));
          }
        } catch {
          useAlert(t('SAAS_BILLING.PAYMENT.ERROR'));
        }
      },
      onError: () => {
        useAlert(t('SAAS_BILLING.PAYMENT.ERROR'));
      },
    })
    .render(paypalButtonsRef.value);
};

const selectPlanForPayment = plan => {
  selectedPlan.value = plan;
  selectedMonths.value = 1;
  showPaymentSection.value = true;
  if (paypalLoaded.value) {
    renderPayPalButtons();
  }
};

const loadPayPalScript = async () => {
  try {
    const response = await getPaypalClientId();
    const clientId = response.data.paypal_client_id;
    const isSandbox = response.data.paypal_sandbox;
    if (!clientId) {
      useAlert(t('SAAS_BILLING.PAYMENT.PAYPAL_NOT_CONFIGURED'));
      return;
    }
    if (window.paypal) {
      paypalLoaded.value = true;
      renderPayPalButtons();
      return;
    }
    const script = document.createElement('script');
    const paypalBaseUrl = isSandbox
      ? 'https://www.sandbox.paypal.com/sdk/js'
      : 'https://www.paypal.com/sdk/js';
    script.src = `${paypalBaseUrl}?client-id=${clientId}&currency=USD`;
    script.addEventListener('load', () => {
      paypalLoaded.value = true;
      renderPayPalButtons();
    });
    script.addEventListener('error', () => {
      useAlert(t('SAAS_BILLING.PAYMENT.PAYPAL_LOAD_ERROR'));
    });
    document.body.appendChild(script);
  } catch {
    useAlert(t('SAAS_BILLING.PAYMENT.PAYPAL_NOT_CONFIGURED'));
  }
};

onMounted(async () => {
  await store.dispatch('saasBilling/fetchSubscription');
  await loadPayPalScript();
});
</script>

<template>
  <div class="grid max-w-4xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader
      :title="$t('SAAS_BILLING.TITLE')"
      :description="$t('SAAS_BILLING.DESCRIPTION')"
      class="mb-4"
    />

    <woot-loading-state v-if="isFetching" />

    <template v-else>
      <!-- Plan actual -->
      <SectionLayout
        :title="$t('SAAS_BILLING.CURRENT_PLAN.TITLE')"
        class="!pt-0"
      >
        <div
          class="p-6 border rounded-xl mb-6"
          :class="
            isExpired
              ? 'border-red-300 bg-red-50'
              : daysRemaining !== null && daysRemaining <= 7
                ? 'border-amber-300 bg-amber-50'
                : 'border-n-slate-6 bg-n-slate-1'
          "
        >
          <div v-if="subscription" class="flex items-start justify-between">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <h3 class="text-lg font-semibold text-n-slate-12">
                  {{ subscription.plan.name }}
                </h3>
                <span
                  class="px-2 py-1 text-xs font-medium rounded-md"
                  :class="statusBadgeClass"
                >
                  {{ statusLabel }}
                </span>
              </div>

              <p
                class="text-sm mt-1"
                :class="
                  isExpired ? 'text-red-700 font-medium' : 'text-n-slate-11'
                "
              >
                {{
                  isExpired
                    ? `${$t('SAAS_BILLING.CURRENT_PLAN.EXPIRED_ON')}: ${formatDate(subscription.expires_at)}`
                    : `${$t('SAAS_BILLING.CURRENT_PLAN.EXPIRES_ON')}: ${formatDate(subscription.expires_at)}`
                }}
              </p>

              <p
                v-if="!isExpired && daysRemaining !== null"
                class="text-sm mt-1"
                :class="
                  daysRemaining <= 7
                    ? 'text-amber-700 font-medium'
                    : 'text-n-slate-11'
                "
              >
                <Icon name="i-lucide-clock" class="w-3.5 h-3.5 inline mr-1" />
                {{
                  $t('SAAS_BILLING.CURRENT_PLAN.DAYS_REMAINING', {
                    days: daysRemaining,
                  })
                }}
              </p>
            </div>

            <div class="text-right">
              <p class="text-2xl font-bold text-n-slate-12">
                {{
                  $t('SAAS_BILLING.PLANS.PRICE', {
                    amount: subscription.plan.price_month,
                  })
                }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ $t('SAAS_BILLING.PLANS.PER_MONTH') }}
              </p>
            </div>
          </div>

          <div v-else class="text-center py-4 text-n-slate-11">
            <Icon
              name="i-lucide-package-x"
              class="w-8 h-8 mx-auto mb-2 opacity-50"
            />
            <p>{{ $t('SAAS_BILLING.CURRENT_PLAN.NO_PLAN') }}</p>
          </div>
        </div>
      </SectionLayout>

      <!-- Planes disponibles -->
      <SectionLayout :title="$t('SAAS_BILLING.PLANS.TITLE')">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="plan in plans"
            :key="plan.id"
            class="p-5 border rounded-xl flex flex-col gap-3 transition-all"
            :class="
              subscription && subscription.plan.id === plan.id
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-slate-6 bg-n-slate-1 hover:border-n-slate-8'
            "
          >
            <div class="flex items-start justify-between">
              <div>
                <h4 class="font-semibold text-n-slate-12">{{ plan.name }}</h4>
                <p class="text-xs text-n-slate-11 mt-0.5 line-clamp-2">
                  {{ plan.description }}
                </p>
              </div>
              <div class="text-right shrink-0 ml-2">
                <p class="text-xl font-bold text-n-slate-12">
                  {{
                    $t('SAAS_BILLING.PLANS.PRICE', { amount: plan.price_month })
                  }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ $t('SAAS_BILLING.PLANS.PER_MONTH') }}
                </p>
              </div>
            </div>

            <ul
              v-if="plan.features && plan.features.length"
              class="text-xs text-n-slate-11 space-y-1"
            >
              <li
                v-for="(feature, i) in plan.features.slice(0, 4)"
                :key="i"
                class="flex items-center gap-1.5"
              >
                <Icon
                  name="i-lucide-check"
                  class="w-3 h-3 text-green-600 shrink-0"
                />
                {{ feature }}
              </li>
            </ul>

            <button
              class="mt-auto w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors"
              :class="
                subscription && subscription.plan.id === plan.id
                  ? 'bg-n-brand text-white hover:bg-n-brand/90'
                  : 'bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4'
              "
              @click="selectPlanForPayment(plan)"
            >
              {{
                subscription && subscription.plan.id === plan.id
                  ? $t('SAAS_BILLING.PLANS.SELECT') + ' (Renovar)'
                  : $t('SAAS_BILLING.PLANS.SELECT')
              }}
            </button>
          </div>
        </div>
      </SectionLayout>

      <!-- Sección de pago -->
      <SectionLayout
        v-if="showPaymentSection && selectedPlan"
        :title="$t('SAAS_BILLING.PAYMENT.TITLE')"
      >
        <div class="p-6 border border-n-slate-6 rounded-xl bg-n-slate-1">
          <div class="mb-4 p-4 bg-n-slate-2 rounded-lg">
            <p class="text-sm font-medium text-n-slate-12">
              {{ selectedPlan.name }}
            </p>
          </div>

          <div class="mb-5">
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ $t('SAAS_BILLING.PAYMENT.MONTHS_LABEL') }}
            </label>
            <select
              v-model.number="selectedMonths"
              class="w-full px-4 py-2.5 border border-n-slate-6 rounded-lg bg-n-slate-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              @change="renderPayPalButtons"
            >
              <option :value="1">{{ $t('SAAS_BILLING.DURATION.1') }}</option>
              <option :value="3">{{ $t('SAAS_BILLING.DURATION.3') }}</option>
              <option :value="6">{{ $t('SAAS_BILLING.DURATION.6') }}</option>
              <option :value="12">{{ $t('SAAS_BILLING.DURATION.12') }}</option>
            </select>
          </div>

          <div
            class="mb-5 p-4 bg-n-brand/5 rounded-lg border border-n-brand/20 flex items-center justify-between"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('SAAS_BILLING.PAYMENT.TOTAL') }}
            </span>
            <span class="text-2xl font-bold text-n-brand">
              {{
                $t('SAAS_BILLING.PAYMENT.TOTAL_AMOUNT', {
                  amount: totalAmount,
                })
              }}</span
            >
          </div>

          <div ref="paypalButtonsRef" class="min-h-[50px]" />

          <div
            v-if="isProcessingPayment"
            class="text-center py-4 text-sm font-medium text-n-brand flex items-center justify-center gap-2"
          >
            <div
              class="w-4 h-4 border-2 border-n-brand border-t-transparent rounded-full animate-spin"
            />
            {{ $t('SAAS_BILLING.PAYMENT.PROCESSING') }}
          </div>

          <button
            class="mt-4 w-full px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12"
            @click="
              showPaymentSection = false;
              selectedPlan = null;
            "
          >
            {{ $t('SAAS_BILLING.PAYMENT.CANCEL') }}
          </button>
        </div>
      </SectionLayout>
    </template>
  </div>
</template>
